import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import swal from "sweetalert";
import { API, ROUTER } from "../../constants/env";
import { formatProfilName } from "../../utils/displayName";
import CarnetSTable from "./CarnetSTable";
import Smoyenne from "./Smoyenne";
import SmoyenneG from "./SmoyenneG";
import StatusChip from "./StatusChip";
import SkeletonLoader from "./SkeletonLoader";
import PdfPreviewModal from "./PdfPreviewModal";
import { displayGrade } from "../../utils/grade";
import "./Carnet.css";

const CarnetS = ({ }) => {
  const { id } = useParams();
  const history = useHistory();
  const [carnet, setCarnet] = useState(null);
  const [classmates, setClassmates] = useState([]);
  const [classmatesLoaded, setClassmatesLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState("saved");
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('carnet_view_mode_v2') || 'focus';
  });
  const [activeModuleIndex, setActiveModuleIndex] = useState(0);
  const [lastSaveAt, setLastSaveAt] = useState(null);
  const [, setTick] = useState(0);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [classMinMax, setClassMinMax] = useState(null);
  const skipFirstChange = useRef(true);
  const dirtyVersion = useRef(0);
  const savedVersion = useRef(0);
  const currentVersionRef = useRef(0);
  const inFlightRef = useRef(false);
  const pendingSaveRef = useRef(false);
  const conflictRef = useRef(false);
  const latestCarnetRef = useRef(null);

  useEffect(() => {
    fetch(API.carnetStudent.info(id), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": localStorage.getItem("token"),
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.error) {
          return swal("Error", response.message, "error");
        }
        currentVersionRef.current = response.version || 0;
        if (response.units) response.units.forEach((u) => (u.subjects || []).forEach((sj) => { if (sj.dispense) sj.note = "D"; }));
        setCarnet(response);
      })
      .catch((e) => {
        return swal("Error", e.message, "error");
      });
  }, []);

  useEffect(() => {
    latestCarnetRef.current = carnet;
  });

  useEffect(() => {
    if (!carnet) return;
    const classeId = carnet.student?.classe?._id;
    if (!classeId) return;
    setClassmatesLoaded(false);
    const tris = localStorage.getItem("tris");
    let cancelled = false;
    fetch(
      API.carnetStudent.list + `?filterByClasse=${classeId}&count=100&searchQuery=`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": localStorage.getItem("token"),
        },
      }
    )
      .then((res) => res.json())
      .then((response) => {
        if (cancelled) return;
        if (response.error) {
          console.warn('Failed to load classmates:', response.message);
          setClassmatesLoaded(true);
          return;
        }
        const list = response.data
          .map((elem) => {
            const card = elem.reportCards.find((r) => r.semestre === tris);
            if (!card) return null;
            const name = formatProfilName(elem.student.profil);
            return { reportCardId: card._id, studentName: name };
          })
          .filter(Boolean);
        setClassmates(list);
        setClassmatesLoaded(true);
      })
      .catch((err) => {
        console.error("Failed to load classmates:", err);
        setClassmatesLoaded(true);
      });
    return () => { cancelled = true; };
  }, [carnet?.student?.classe?._id]);

  useEffect(() => {
    if (!carnet) return;
    const classeId = carnet.student?.classe?._id;
    const semestre = localStorage.getItem("tris");
    if (!classeId || !semestre) return;
    let cancelledMinMax = false;
    fetch(API.classMinMax.get(classeId, semestre), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": localStorage.getItem("token"),
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (cancelledMinMax) return;
        if (response.error) {
          console.warn("classMinMax fetch error:", response.message);
          return;
        }
        setClassMinMax(response);
      })
      .catch((err) => {
        if (cancelledMinMax) return;
        console.warn("classMinMax fetch failed:", err);
      });
    return () => { cancelledMinMax = true; };
  }, [carnet?.student?.classe?._id]);

  useEffect(() => {
    if (!carnet) return;
    if (skipFirstChange.current) { skipFirstChange.current = false; return; }
    if (conflictRef.current) return;
    dirtyVersion.current += 1;
    const myVersion = dirtyVersion.current;
    setSaveStatus("unsaved");
    const timer = setTimeout(() => saveCarnet(false, myVersion), 2000);
    return () => clearTimeout(timer);
  }, [carnet]);

  useEffect(() => {
    localStorage.setItem('carnet_view_mode_v2', viewMode);
  }, [viewMode]);

  // Note: force re-render toutes les 15s pour rafraîchir timeAgo()
  // Optimisation possible: extraire dans <TimeAgo> sous-composant si lag perçu
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 15000);
    return () => clearInterval(id);
  }, []);

  const noteToNumber = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = parseFloat(String(v).replace(",", "."));
    return isNaN(n) ? null : n;
  };

  const roundN = (num) => {
    if (num === null || num === undefined || isNaN(num)) return 0;
    const str = num.toString();
    const dotIdx = str.indexOf(".");
    if (dotIdx === -1) return Number(str);
    return Number(str.slice(0, dotIdx + 3));
  };

  const changeCarnetRemarque = (data) => {
    setCarnet({ ...carnet, ["remarque"]: data });
  };

  const changeModuleRemarque = (unitInedx, data) => {
    const units = carnet.units;
    units[unitInedx].unitRemarque = data;
    setCarnet({ ...carnet, units });
  };

  const changeNoteEdge = (unitInedx, subjectIndex, note, edge) => {
    if (typeof note === "string" && note.length > 1 && note.charAt(0) === "0" && note.charAt(1) !== ",")
      note = note.substring(1);
    const { units } = carnet;
    const { subjects } = units[unitInedx];
    if (edge === "max") subjects[subjectIndex].max = note;
    else subjects[subjectIndex].min = note;
    setCarnet({ ...carnet, units });
  };

  const changeNote = (unitInedx, subjectIndex, note) => {
    const _isDisp = typeof note === "string" && (note.toUpperCase() === "D" || note === "-");
    if (!_isDisp && typeof note === "string" && note.length > 1 && note.charAt(0) === "0" && note.charAt(1) !== ",")
      note = note.substring(1);
    const units = carnet.units;
    const subjects = units[unitInedx].subjects;
    subjects[subjectIndex].note = _isDisp ? "D" : note;
    subjects[subjectIndex].dispense = _isDisp;
    const validNotes = subjects.filter(s => !s.dispense).map(s => noteToNumber(s.note)).filter(n => n !== null);
    units[unitInedx].unitMoyenne = validNotes.length
      ? roundN(validNotes.reduce((acc, n) => acc + n, 0) / validNotes.length)
      : 0;
    const moyenne = moyenneCarnet(units);
    setCarnet({ ...carnet, units, moyenne });
  };

  const moyenneCarnet = (units) => {
    const languageUnits = units.filter(
      (unit) =>
        unit.unitName === "مجال اللغة الفرنسية" ||
        unit.unitName === "مجال اللغة الانجليزية"
    );
    const languageCoef = languageUnits.reduce((result, unit) => {
      return unit.unitCoef + result;
    }, 0);

    const languageMoyenne = languageCoef
      ? roundN(
          languageUnits.reduce((result, unit) => {
            return result + (unit.unitMoyenne || 0) * unit.unitCoef;
          }, 0) / languageCoef
        )
      : 0;

    const MoyenneCor = [
      { moyenne: languageMoyenne, Coef: 1.5, Name: "Language" },
      ...units
        .filter(
          (unit) =>
            unit.unitName !== "مجال اللغة الفرنسية" &&
            unit.unitName !== "مجال اللغة الانجليزية"
        )
        .map((unit) => ({
          moyenne: unit.unitMoyenne || 0,
          Coef: unit.unitCoef,
          Name: unit.unitName,
        })),
    ];

    const moyenneC = MoyenneCor.reduce(
      (result, moy) => ({
        moyenne: result.moyenne + moy.moyenne * moy.Coef,
        Coef: moy.Coef + result.Coef,
      }),
      { moyenne: 0, Coef: 0 }
    );

    return moyenneC.Coef ? moyenneC.moyenne / moyenneC.Coef : 0;
  };

  const saveCarnet = async (showSwal = false, myVersion = null) => {
    if (conflictRef.current) return false;
    if (inFlightRef.current) {
      pendingSaveRef.current = true;
      return true;
    }
    inFlightRef.current = true;
    setSaveStatus("saving");
    const launchedAtVersion = myVersion ?? dirtyVersion.current;
    try {
      const res = await fetch(API.carnetStudent.update(id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": localStorage.getItem("token"),
        },
        body: JSON.stringify({ ...latestCarnetRef.current, version: currentVersionRef.current }),
      });
      const data = await res.json();
      if (res.status === 409) {
        conflictRef.current = true;
        setSaveStatus("conflict");
        return false;
      }
      if (data.error) {
        setSaveStatus("error");
        if (showSwal) swal("Error", data.message, "error");
        return false;
      }
      currentVersionRef.current = data.version;
      savedVersion.current = launchedAtVersion;
      const newStatus = savedVersion.current === dirtyVersion.current ? "saved" : "unsaved";
      setSaveStatus(newStatus);
      if (newStatus === "saved") setLastSaveAt(new Date());
      if (showSwal) swal("Enregistré", "Carnet sauvegardé", "success");
      return true;
    } catch (e) {
      setSaveStatus("error");
      if (showSwal) swal("Error", e.message, "error");
      return false;
    } finally {
      inFlightRef.current = false;
      if (pendingSaveRef.current && !conflictRef.current) {
        pendingSaveRef.current = false;
        saveCarnet(false, dirtyVersion.current);
      }
    }
  };

  const navigateToStudent = async (targetIndex) => {
    if (targetIndex < 0 || targetIndex >= classmates.length) return;
    const ok = await saveCarnet(false);
    if (!ok) {
      const confirmed = await swal({
        title: "Erreur de sauvegarde",
        text: "Les modifications n'ont pas pu être enregistrées. Naviguer quand même ?",
        icon: "warning",
        buttons: {
          cancel: { text: "Annuler", visible: true, value: false },
          confirm: { text: "Naviguer sans sauvegarder", value: true, className: "btn-danger" },
        },
        dangerMode: true,
      });
      if (!confirmed) return;
    }
    const target = classmates[targetIndex];
    history.push(`/carnets/st/${target.reportCardId}/${target.studentName}`);
  };

  const confirmAndFinish = async () => {
    const ok = await saveCarnet(false);
    if (!ok) {
      const confirmed = await swal({
        title: "Erreur de sauvegarde",
        text: "Les modifications n'ont pas pu être enregistrées. Terminer quand même ?",
        icon: "warning",
        buttons: {
          cancel: { text: "Annuler", visible: true, value: false },
          confirm: { text: "Terminer sans sauvegarder", value: true, className: "btn-danger" },
        },
        dangerMode: true,
      });
      if (!confirmed) return;
    }
    history.push(ROUTER.CARNETS.LIST);
  };

  const trimestreMap = { premier: '1', deuxieme: '2', troisieme: '3' };

  const openPdfModal = async () => {
    setShowPdfModal(true);
    setPdfLoading(true);
    setPdfError(null);
    try {
      const response = await fetch(API.generatePdf.student(id), {
        headers: { 'x-auth-token': localStorage.getItem('token') },
      });
      if (!response.ok) throw new Error(`Erreur ${response.status}`);
      const blob = await response.blob();
      setPdfBlobUrl(URL.createObjectURL(blob));
    } catch (err) {
      setPdfError(err.message || 'Erreur lors du chargement du PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdfModal = () => {
    setShowPdfModal(false);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const pdfFilename = () => {
    const firstName = carnet?.student?.profil?.firstName || '';
    const lastName = carnet?.student?.profil?.lastName || id;
    const trisRaw = localStorage.getItem('tris') || '';
    const trisNum = trimestreMap[trisRaw] || '';
    return `carnet-${firstName}-${lastName}${trisNum ? `-T${trisNum}` : ''}.pdf`;
  };

  const timeAgo = (date) => {
    if (!date) return '';
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 5) return "à l'instant";
    if (seconds < 60) return `il y a ${seconds}s`;
    if (seconds < 3600) return `il y a ${Math.floor(seconds / 60)}min`;
    return `il y a ${Math.floor(seconds / 3600)}h`;
  };

  const statusConfig = {
    saved:    { label: "✓ Enregistré",             color: "#15803d" },
    saving:   { label: "⏳ Enregistrement…",        color: "#b45309" },
    unsaved:  { label: "⚠ Non sauvegardé",         color: "#dc2626" },
    error:    { label: "✕ Erreur",                  color: "#dc2626",
                title: "Erreur de sauvegarde — vérifiez votre connexion" },
    conflict: { label: "✕ Conflit — Rafraîchir",   color: "#7f1d1d" },
  };
  const sc = statusConfig[saveStatus] || statusConfig.saved;
  const currentIndex = classmates.findIndex((c) => c.reportCardId === id);
  const isLastStudent = classmates.length > 0 && currentIndex === classmates.length - 1;
  const canNavigateNext = classmates.length > 0 && currentIndex >= 0 && currentIndex < classmates.length - 1;
  const hasClasse = Boolean(carnet?.student?.classe?._id);
  const safeActiveIndex = Math.min(activeModuleIndex, (carnet?.units?.length || 1) - 1);
  const totalSubjects = carnet?.units?.reduce((sum, u) => sum + (u.subjects?.length || 0), 0) || 0;
  const filledSubjects = carnet?.units?.reduce((sum, u) =>
    sum + (u.subjects?.filter(s => noteToNumber(s.note) !== null).length || 0), 0) || 0;
  const recoCount = carnet?.units?.filter(u =>
    u.unitRemarque && u.unitRemarque.trim() !== '' && u.unitRemarque.trim() !== '.'
  ).length || 0;

  const enrichedUnits = useMemo(() => {
    if (!carnet?.units) return [];
    if (!classMinMax?.bySubject) return carnet.units;
    return carnet.units.map((unit) => ({
      ...unit,
      subjects: (unit.subjects || []).map((subj) => {
        const calc = classMinMax.bySubject[subj.subjectId];
        if (!calc) return subj;
        return {
          ...subj,
          min: typeof calc.min === "number" ? calc.min : subj.min,
          max: typeof calc.max === "number" ? calc.max : subj.max,
        };
      }),
    }));
  }, [carnet, classMinMax]);

  if (!carnet) {
    return <SkeletonLoader />;
  }

  return (
    <div>
      {saveStatus === "conflict" && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 200,
          backgroundColor: "rgba(220, 38, 38, 0.06)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "#fff", padding: "24px 32px", borderRadius: 8,
            border: "2px solid #dc2626", textAlign: "center",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)", maxWidth: 420,
          }}>
            <p style={{ color: "#7f1d1d", fontWeight: 600, fontSize: 15, marginBottom: 16, lineHeight: 1.5 }}>
              ⚠ Conflit détecté — Vos modifications n'ont pas pu être enregistrées.<br />
              Rafraîchissez la page pour continuer.
            </p>
            <button className="btn btn-danger" onClick={() => window.location.reload()}>
              Rafraîchir la page
            </button>
          </div>
        </div>
      )}
      <div style={{
        position: "sticky", top: 0, zIndex: 100,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        height: 40, padding: "0 16px", marginBottom: 8,
        backgroundColor: "#f8fafc", borderBottom: "1px solid #e5e7eb",
      }}>
        <StatusChip status={carnet?.status} />
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {hasClasse && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                className="btn-nav-carnet"
                disabled={currentIndex <= 0 || classmates.length === 0}
                title={classmates[currentIndex - 1]?.studentName || ""}
                onClick={() => navigateToStudent(currentIndex - 1)}
              >
                ◀ Précédent
              </button>
              <span style={{ fontSize: 12, color: "#6b7280", minWidth: 48, textAlign: "center" }}>
                {!classmatesLoaded ? "…" : `${currentIndex >= 0 ? currentIndex + 1 : 0} / ${classmates.length}`}
              </span>
              <button
                className="btn-nav-carnet"
                disabled={currentIndex >= classmates.length - 1 || classmates.length === 0}
                title={classmates[currentIndex + 1]?.studentName || ""}
                onClick={() => navigateToStudent(currentIndex + 1)}
              >
                Suivant ▶
              </button>
            </div>
          )}
          <button
            className="btn-pdf-preview"
            onClick={openPdfModal}
            title="Aperçu PDF avant impression"
          >
            📄 Aperçu PDF
          </button>
          <div className="view-toggle">
            <button
              className={viewMode === 'classic' ? 'active' : ''}
              onClick={() => setViewMode('classic')}
              title="Vue classique : tous les modules empilés"
            >
              Classique
            </button>
            <button
              className={viewMode === 'focus' ? 'active' : ''}
              onClick={() => setViewMode('focus')}
              title="Mode focus : un module à la fois avec sidebar"
            >
              Focus
            </button>
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: sc.color }}
                title={sc.title || ""}>
            {sc.label}{lastSaveAt && saveStatus === 'saved' ? ` · ${timeAgo(lastSaveAt)}` : ''}
          </span>
        </div>
      </div>

      {showPdfModal && (
        <PdfPreviewModal
          blobUrl={pdfBlobUrl}
          loading={pdfLoading}
          error={pdfError}
          onClose={closePdfModal}
          onDownload={() => {
            const a = document.createElement('a');
            a.href = pdfBlobUrl;
            a.download = pdfFilename();
            a.click();
          }}
        />
      )}

      <div className="workflow-row">
        <div className={`wf-step ${filledSubjects === totalSubjects ? 'done' : 'in-progress'}`}>
          <span className="dot">
            {filledSubjects === totalSubjects ? '✓' : Math.round((filledSubjects / totalSubjects) * 100) + '%'}
          </span>
          <span className="lbl">Notes · {filledSubjects} / {totalSubjects} matières</span>
        </div>
        <span className="wf-arrow">→</span>
        <div className={`wf-step ${recoCount === carnet.units.length ? 'done' : 'in-progress'}`}>
          <span className="dot">{recoCount === carnet.units.length ? '✓' : recoCount}</span>
          <span className="lbl">Recommandations · {recoCount} / {carnet.units.length} modules</span>
        </div>
      </div>

      <div className="carnet-content">
        {viewMode === 'classic' ? (
          <>
            {enrichedUnits.map((unit, index) => (
              <React.Fragment key={index}>
                <CarnetSTable
                  changeNote={changeNote}
                  unit={unit}
                  setCarnet={setCarnet}
                  unitInedx={index}
                  changeModuleRemarque={changeModuleRemarque}
                  changeNoteEdge={changeNoteEdge}
                />
                <br />
              </React.Fragment>
            ))}
          </>
        ) : (
          <div className="focus-layout">
            <aside className="modules-sidebar">
              {carnet.units.map((unit, i) => (
                <button
                  key={i}
                  className={`module-link ${i === safeActiveIndex ? 'active' : ''}`}
                  onClick={() => setActiveModuleIndex(i)}
                >
                  <span className="module-name">{unit.unitName}</span>
                  <span className="module-moyenne">
                    {unit.unitMoyenne != null ? displayGrade(unit.unitMoyenne) : '—'}
                  </span>
                </button>
              ))}
            </aside>
            <div className="focus-panel">
              <CarnetSTable
                changeNote={changeNote}
                unit={enrichedUnits[safeActiveIndex]}
                setCarnet={setCarnet}
                unitInedx={safeActiveIndex}
                changeModuleRemarque={changeModuleRemarque}
                changeNoteEdge={changeNoteEdge}
              />
            </div>
          </div>
        )}
      </div>

      <Smoyenne
        general
        color="#b87006"
        remarque={carnet?.remarque}
        changeCarnetRemarque={changeCarnetRemarque}
      />
      <SmoyenneG
        minmax={localStorage.getItem("tris") !== "troisieme"}
        studentId={carnet?.student._id}
        classeId={carnet?.student?.classe?._id}
        carnetMoyenne={carnet?.moyenne}
        isLastStudent={isLastStudent}
        saveCarnet={
          canNavigateNext
            ? () => navigateToStudent(currentIndex + 1)
            : isLastStudent
              ? () => confirmAndFinish()
              : () => saveCarnet(true)
        }
      />
    </div>
  );
};

export default CarnetS;
